import { useState, useEffect, useMemo } from "react";
import { fetchAuth } from "../common/api.jsx";

export const GLOBAL_DRILL = "__global__";
export const LATEST_DATE = "__latest__";

export function useInternalLoadDrills(season) {
  const [dates, setDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [loadingDrills, setLoadingDrills] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState(GLOBAL_DRILL);
  const [selectedPeriod, setSelectedPeriod] = useState("AM");

  // Fetch dates from internal endpoint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDates(true);
      try {
        const res = await fetchAuth(`/internal_load/dates`);
        if (!res.ok) throw new Error("fetch internal dates failed");
        const data = await res.json();
        if (!cancelled) {
          setDates(data.dates ?? []);
        }
      } catch (e) {
        console.error("useInternalLoadDrills dates error:", e);
      } finally {
        if (!cancelled) setLoadingDates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [season]);

  // Auto-select most recent date once dates are loaded
  useEffect(() => {
    if (dates.length > 0 && selectedDate == null) {
      setSelectedDate(dates[0]);
    }
  }, [dates]);

  // Fetch drills for selected date
  useEffect(() => {
    if (!selectedDate) {
      setDrillData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDrills(true);
      try {
        const res = await fetchAuth(`/internal_load/drills?date=${selectedDate}`);
        if (!res.ok) throw new Error("fetch internal drills failed");
        const data = await res.json();
        
      // AJOUTEZ CE LOG

        if (!cancelled) {
          setDrillData(data);
          setSelectedDrill(GLOBAL_DRILL);
          const periods = data.available_periods ?? [];
          setSelectedPeriod(periods.includes("AM") ? "AM" : (periods[0] ?? "AM"));
        }
      } catch (e) {
        console.error("useInternalLoadDrills drills error:", e);
      } finally {
        if (!cancelled) setLoadingDrills(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate]);

  const availablePeriods = useMemo(() => drillData?.available_periods ?? [], [drillData]);

  const drillNames = useMemo(() => {
    if (!drillData?.players) return [];
    const set = new Set();
    Object.values(drillData.players).forEach((p) =>
      p.drills?.forEach((d) => {
        if (d.period === selectedPeriod) set.add(d.name);
      })
    );
    return Array.from(set).sort();
  }, [drillData, selectedPeriod]);

  const playerMetrics = useMemo(() => {
    if (!drillData?.players) return {};

const internalKeys = [
  "trainability",      // ← pour trainability_index
  "mechanical_fatigue", // ← pour mechanical_fatigue
  "intensity",         // ← pour intensity_index (car dans drills c'est "intensity")
  "mechanical_load",   // ← pour mechanical_load_index (car dans drills c'est "mechanical_load")
  "RII",               // ← pour RII
  "MLI",               // ← pour MLI
];
    const out = {};
    for (const [pid, pdata] of Object.entries(drillData.players)) {
      const periodDrills = (pdata.drills ?? []).filter((d) => d.period === selectedPeriod);
      if (periodDrills.length === 0) continue;

      if (selectedDrill === GLOBAL_DRILL) {
        // Pour les métriques internes, on prend la moyenne (pas la somme)
        const totals = {};
        for (const k of internalKeys) {
          const vals = periodDrills.map((d) => d[k]).filter((v) => v != null && !isNaN(Number(v)));
          
          totals[k] = vals.length ? vals.reduce((a, b) => a + Number(b), 0) / vals.length : null;
        }
        out[pid] = { ...pdata, metrics: totals };
     
      } else {
        const matched = periodDrills.filter((d) => d.name === selectedDrill);
        if (matched.length === 0) {
          out[pid] = { ...pdata, metrics: {} };
        } else {
          const agg = {};
          for (const k of internalKeys) {
            const vals = matched.map((d) => d[k]).filter((v) => v != null);
            agg[k] = vals.length ? vals.reduce((a, b) => a + Number(b), 0) / vals.length : null;
          }
          out[pid] = { ...pdata, metrics: agg };
                  if (pdata.first_name === "Moussa" && pdata.last_name === "DIARRA") {
          console.log(`\n${"=".repeat(60)}`);
          console.log(`🖱️  CLIC SUR LE DRILL: "${selectedDrill}"`);
          console.log(`${"=".repeat(60)}`);
          console.log(`👤 Joueur: ${pdata.first_name} ${pdata.last_name}`);
          console.log(`📍 Position: ${pdata.position}`);
          console.log(`⏰ Période: ${selectedPeriod}`);
          console.log(`📊 Nombre de drills matchés: ${matched.length}\n`);
          
          console.log(`📈 VALEURS POUR "${selectedDrill}":`);
          console.log(`   ┌─────────────────────────────────────────┐`);
          console.log(`   │ trainability:        ${String(agg.trainability).padEnd(20)} │`);
          console.log(`   │ mechanical_fatigue:  ${String(agg.mechanical_fatigue).padEnd(20)} │`);
          console.log(`   │ intensity:           ${String(agg.intensity).padEnd(20)} │`);
          console.log(`   │ mechanical_load:     ${String(agg.mechanical_load).padEnd(20)} │`);
          console.log(`   │ RII:                 ${String(agg.RII).padEnd(20)} │`);
          console.log(`   │ MLI:                 ${String(agg.MLI).padEnd(20)} │`);
          console.log(`   └─────────────────────────────────────────┘\n`);
          
          // Afficher aussi les valeurs brutes du drill si plusieurs matchs
          if (matched.length > 1) {
            console.log(`📋 DÉTAIL DES ${matched.length} MATCHS:`);
            matched.forEach((drill, idx) => {
              console.log(`   ${idx + 1}. ${drill.name}:`);
              console.log(`      intensity: ${drill.intensity}, mechanical_load: ${drill.mechanical_load}`);
            });
            console.log();
          }
        }
        
      }
      }
    }
    return out;
  }, [drillData, selectedDrill, selectedPeriod]);

  return {
    dates,
    loadingDates,
    selectedDate,
    setSelectedDate,
    drillData,
    loadingDrills,
    selectedDrill,
    setSelectedDrill,
    drillNames,
    playerMetrics,
    availablePeriods,
    selectedPeriod,
    setSelectedPeriod,
  };
}